import { getUserIdFromToken } from '@tracedocuments/auth';

function userScope(): string {
  const userId = getUserIdFromToken();
  return userId ?? 'anon';
}

export const queryKeys = {
  auth: {
    me: () => ['auth', 'me', userScope()] as const,
  },
  documentShares: {
    inbox: (page?: number, pageSize?: number) =>
      ['document-shares', 'inbox', userScope(), page ?? 1, pageSize ?? 50] as const,
    sent: (page?: number, pageSize?: number, archived = false) =>
      ['document-shares', 'sent', userScope(), page ?? 1, pageSize ?? 50, archived] as const,
    pendingCount: () => ['document-shares', 'pending-count', userScope()] as const,
    detail: (id: string) => ['document-shares', 'detail', userScope(), id] as const,
  },
  documents: {
    all: () => ['documents', userScope()] as const,
    list: (page?: number, pageSize?: number) =>
      ['documents', 'list', userScope(), page ?? 'all', pageSize ?? 'all'] as const,
    /** Prefix for invalidating both page-1 and infinite document lists. */
    listPrefix: () => ['documents', 'list', userScope()] as const,
    listInfinite: (pageSize = 50) =>
      ['documents', 'list', userScope(), 'infinite', pageSize] as const,
    sentDeliveries: (page?: number, pageSize?: number, archived = false) =>
      ['documents', 'sent-deliveries', userScope(), page ?? 1, pageSize ?? 50, archived] as const,
    stats: () => ['documents', 'stats', userScope()] as const,
    detail: (id: string) => ['documents', 'detail', userScope(), id] as const,
    deletionRequests: (id: string) =>
      ['documents', 'deletion-requests', userScope(), id] as const,
  },
  dashboard: {
    insights: () => ['dashboard', 'insights', userScope()] as const,
    summary: () => ['dashboard', 'summary', userScope()] as const,
  },
  billing: {
    planUsage: () => ['billing', 'plan-usage', userScope()] as const,
    plans: () => ['billing', 'plans', userScope()] as const,
    subscription: () => ['billing', 'subscription', userScope()] as const,
    creditPackages: () => ['billing', 'credit-packages', userScope()] as const,
    creditBalance: () => ['billing', 'credits', userScope()] as const,
    creditHistory: (pageSize = 20, type?: string, status?: string) =>
      ['billing', 'credit-history', userScope(), pageSize, type ?? '', status ?? ''] as const,
    paymentMethods: () => ['billing', 'payment-methods', userScope()] as const,
    creditCosts: () => ['billing', 'credit-costs', userScope()] as const,
    planFeatures: () => ['billing', 'plan-features', userScope()] as const,
  },
  storage: {
    summary: () => ['storage', 'summary', userScope()] as const,
    documents: () => ['storage', 'documents', userScope()] as const,
  },
  notifications: {
    all: () => ['notifications'] as const,
    unreadCount: () => ['notifications', 'unread-count', userScope()] as const,
    list: (pageSize = 20, unreadOnly = false) =>
      ['notifications', 'list', userScope(), pageSize, unreadOnly] as const,
  },
  conversations: {
    list: (page?: number, pageSize?: number, archived = false) =>
      ['conversations', 'list', userScope(), page ?? 1, pageSize ?? 50, archived] as const,
    messages: (conversationId: string) =>
      ['conversations', 'messages', userScope(), conversationId] as const,
    pinned: (conversationId: string) =>
      ['conversations', 'pinned', userScope(), conversationId] as const,
    readiness: () => ['conversations', 'readiness', userScope()] as const,
    delivery: (documentId: string, recipientId?: string | null, shareId?: string | null) =>
      ['conversations', 'delivery', userScope(), documentId, recipientId ?? '', shareId ?? ''] as const,
  },
  settings: {
    protectionPresets: () => ['settings', 'protection-presets', userScope()] as const,
    watermarkTemplates: () => ['settings', 'watermark-templates', userScope()] as const,
    branding: () => ['settings', 'branding', userScope()] as const,
    recipientLists: () => ['settings', 'recipient-lists', userScope()] as const,
    sendDefaults: () => ['settings', 'send-defaults', userScope()] as const,
    protectionPresetsManage: () =>
      ['settings', 'protection-presets-manage', userScope()] as const,
    privacy: () => ['settings', 'privacy', userScope()] as const,
    blockedUsers: () => ['settings', 'blocked-users', userScope()] as const,
    loginHistory: (pageSize = 20) =>
      ['settings', 'login-history', userScope(), pageSize] as const,
  },
  support: {
    tickets: (page = 1, pageSize = 50) =>
      ['support', 'tickets', userScope(), page, pageSize] as const,
  },
  generation: {
    job: (documentId: string, jobId?: string) =>
      ['generation', 'job', userScope(), documentId, jobId ?? 'active'] as const,
  },
  admin: {
    all: () => ['admin', userScope()] as const,
    dashboardPage: () => ['admin', 'dashboard-page', userScope()] as const,
    documentActivitySummary: () => ['admin', 'document-activity-summary', userScope()] as const,
    users: (page?: number, search?: string, pageSize?: number, accessStatus?: string, adminTag?: string) =>
      ['admin', 'users', userScope(), page ?? 1, search ?? '', pageSize ?? 25, accessStatus ?? '', adminTag ?? ''] as const,
    /** Prefix for invalidating the users list regardless of active page/search/accessStatus filter. */
    usersPrefix: () => ['admin', 'users', userScope()] as const,
    tickets: (page?: number, status?: number | 'all', pageSize?: number) =>
      ['admin', 'tickets', userScope(), page ?? 1, status ?? 'all', pageSize ?? 25] as const,
    documents: (page?: number, search?: string, pageSize?: number, status?: string) =>
      ['admin', 'documents', userScope(), page ?? 1, search ?? '', pageSize ?? 25, status ?? 'all'] as const,
    /** Prefix for invalidating the documents list regardless of active page/search filter. */
    documentsPrefix: () => ['admin', 'documents', userScope()] as const,
    incidents: (page?: number, pageSize?: number) =>
      ['admin', 'incidents', userScope(), page ?? 1, pageSize ?? 50] as const,
    incidentDetail: (id: string) => ['admin', 'incident', userScope(), id] as const,
    applicationErrors: (page?: number) =>
      ['admin', 'application-errors', userScope(), page ?? 1] as const,
    documentActivity: (limit?: number) =>
      ['admin', 'document-activity', userScope(), limit ?? 50] as const,
    operationsCenter: () => ['admin', 'operations-center', userScope()] as const,
    runbook: () => ['admin', 'runbook', userScope()] as const,
    globalSearch: (q: string) => ['admin', 'search', userScope(), q] as const,
    userDetail: (userId: string) => ['admin', 'user-detail', userScope(), userId] as const,
    auditLogs: (page?: number, action?: string) =>
      ['admin', 'audit', userScope(), page ?? 1, action ?? ''] as const,
    dunning: () => ['admin', 'dunning', userScope()] as const,
    invoiceSummary: () => ['admin', 'invoice-summary', userScope()] as const,
    safetyReportsSummary: () => ['admin', 'safety-reports-summary', userScope()] as const,
    invoices: (page?: number, needsAttachment?: boolean | 'all', search?: string) =>
      ['admin', 'invoices', userScope(), page ?? 1, needsAttachment ?? 'all', search ?? ''] as const,
    invoiceDetail: (id: string) => ['admin', 'invoice', userScope(), id] as const,
    missingInvoices: (page?: number) => ['admin', 'missing-invoices', userScope(), page ?? 1] as const,
    breakGlass: () => ['admin', 'break-glass', userScope()] as const,
    ticketDetail: (id: string) => ['admin', 'ticket-detail', userScope(), id] as const,
    opsBadgeCount: () => ['admin', 'ops-badge-count', userScope()] as const,
    scope: (name: string, ...params: unknown[]) =>
      ['admin', name, userScope(), ...params] as const,
  },
} as const;
